import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyledFormControl, StyledMenuItem } from "@/components/ui";
import { useApiMutation } from "../../hooks/use-api-mutation";
import {
  type APIKey,
  type AppConfig,
  type AppConfigType,
  TestRunStatus,
  ViewportType,
} from "../../types.ts";
import { ValidatedUrlInput } from "../validated-url-input";

// Unified 14px font size for text fields in this dialog
const textFieldSx = {
  "& .MuiInputBase-input": { fontSize: 14 },
  "& .MuiInputLabel-root": { fontSize: 14 },
  "& .MuiFormHelperText-root": { fontSize: 12 },
} as const;

interface AppConfigDialogProps {
  open: boolean;
  onClose: () => void;
  config?: AppConfig;
  onSubmit: (data: Partial<AppConfig>) => void;
  isLoading: boolean;
  isEdit?: boolean;
}

export default function AppConfigDialog({
  open,
  onClose,
  config,
  onSubmit,
  isLoading,
  isEdit = false,
}: AppConfigDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    cfg_type: "production" as AppConfigType,
    base_url: "",
    login_url: "",
    login_username: "",
    login_password: "",
    login_mfa_secret: "",
    login_instructions: "",
    vp_type: ViewportType.DESKTOP,
    failure_status: TestRunStatus.FAILED as TestRunStatus.FAILED | TestRunStatus.FAILED_PENDING,
    remove_popups: false,
    // Advanced settings
    user_agent: "",
    locale: "",
    timezone: "",
    geolocation_latitude: "",
    geolocation_longitude: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showMfaSecret, setShowMfaSecret] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiKey, setApiKey] = useState<APIKey | null>(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [advancedSettingsExpanded, setAdvancedSettingsExpanded] = useState(false);
  const advancedSettingsRef = React.useRef<HTMLDivElement>(null);

  // Track URL validation state
  const [baseUrlValid, setBaseUrlValid] = useState(false);
  const [loginUrlValid, setLoginUrlValid] = useState(true); // true by default since login URL is optional

  // Warning dialog for invalid URLs
  const [showUrlWarningDialog, setShowUrlWarningDialog] = useState(false);
  const [urlWarnings, setUrlWarnings] = useState<string[]>([]);

  const { enqueueSnackbar } = useSnackbar();

  const { mutate: getOrCreateApiKey, isPending: isGettingApiKey } = useApiMutation<void, APIKey>(
    "GET",
    "/api-key",
    {
      onSuccess: (apiKeyData) => {
        setApiKey(apiKeyData);
        setHasApiKey(true);
        navigator.clipboard.writeText(apiKeyData.key);
        enqueueSnackbar("API key copied to clipboard", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to get API key: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  useEffect(() => {
    if (config && isEdit) {
      setFormData({
        name: config.name || "",
        cfg_type: config.cfg_type || "production",
        base_url: config.base_url || "",
        login_url: config.login_url || "",
        login_username: config.login_username || "",
        login_password: config.login_password || "",
        login_mfa_secret: config.login_mfa_secret || "",
        login_instructions: config.login_instructions || "",
        vp_type: config.vp_type || ViewportType.DESKTOP,
        failure_status:
          config.failure_status ||
          (TestRunStatus.FAILED as TestRunStatus.FAILED | TestRunStatus.FAILED_PENDING),
        remove_popups: config.remove_popups || false,
        // Advanced settings
        user_agent: config.user_agent || "",
        locale: config.locale || "",
        timezone: config.timezone || "",
        geolocation_latitude: config.geolocation?.latitude?.toString() || "",
        geolocation_longitude: config.geolocation?.longitude?.toString() || "",
      });
      setApiKey(config.api_keys?.[0] || null);
      setHasApiKey(!!config.api_keys?.[0]);
      // Initialize validation state for edit mode (assume existing URLs are valid)
      setBaseUrlValid(!!config.base_url);
      setLoginUrlValid(!config.login_url || !!config.login_url);
    } else if (!isEdit) {
      setFormData({
        name: "",
        cfg_type: "production",
        base_url: "",
        login_url: "",
        login_username: "",
        login_password: "",
        login_mfa_secret: "",
        login_instructions: "",
        vp_type: ViewportType.DESKTOP,
        failure_status: TestRunStatus.FAILED as TestRunStatus.FAILED | TestRunStatus.FAILED_PENDING,
        remove_popups: false,
        // Advanced settings
        user_agent: "",
        locale: "",
        timezone: "",
        geolocation_latitude: "",
        geolocation_longitude: "",
      });
      setApiKey(null);
      setHasApiKey(false);
      // Reset validation state for new config
      setBaseUrlValid(false);
      setLoginUrlValid(true);
    }
  }, [config, isEdit]);

  const handleClose = useCallback(() => {
    setErrors({});
    setShowPassword(false);
    setShowMfaSecret(false);
    setApiKey(null);
    onClose();
  }, [onClose]);

  const dialogOnClose = handleClose;

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleClose]);

  const addProtocolIfMissing = useCallback((url: string): string => {
    if (!url.trim()) return url;
    if (url.match(/^https?:\/\//)) return url;
    return `https://${url}`;
  }, []);

  const validateForm = useMemo(() => {
    return () => {
      const newErrors: Record<string, string> = {};

      if (!formData.name.trim()) {
        newErrors.name = "Name is required";
      }

      if (!formData.base_url.trim()) {
        newErrors.base_url = "Base URL is required";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };
  }, [formData]);

  const submitForm = useCallback(() => {
    // Automatically add https:// if protocol is missing
    const processedData: Partial<AppConfig> = {
      name: formData.name,
      cfg_type: formData.cfg_type,
      base_url: addProtocolIfMissing(formData.base_url),
      login_url: formData.login_url ? addProtocolIfMissing(formData.login_url) : formData.login_url,
      login_username: formData.login_username,
      login_password: formData.login_password,
      login_mfa_secret: formData.login_mfa_secret,
      login_instructions: formData.login_instructions,
      vp_type: formData.vp_type,
      failure_status: formData.failure_status,
      remove_popups: formData.remove_popups,
    };

    // Add advanced settings if provided
    if (formData.user_agent) {
      processedData.user_agent = formData.user_agent;
    }
    if (formData.locale) {
      processedData.locale = formData.locale;
    }
    if (formData.timezone) {
      processedData.timezone = formData.timezone;
    }
    if (formData.geolocation_latitude && formData.geolocation_longitude) {
      processedData.geolocation = {
        latitude: parseFloat(formData.geolocation_latitude),
        longitude: parseFloat(formData.geolocation_longitude),
      };
    }

    onSubmit(processedData);
  }, [formData, onSubmit, addProtocolIfMissing]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (validateForm()) {
      // Check for URL validation warnings
      const warnings: string[] = [];
      if (!baseUrlValid && formData.base_url) {
        warnings.push("Base URL could not be reached or returned an error");
      }
      if (!loginUrlValid && formData.login_url) {
        warnings.push("Login URL could not be reached or returned an error");
      }

      // If there are URL warnings, show confirmation dialog
      if (warnings.length > 0) {
        setUrlWarnings(warnings);
        setShowUrlWarningDialog(true);
        return;
      }

      submitForm();
    }
  };

  const handleConfirmWithWarnings = () => {
    setShowUrlWarningDialog(false);
    setUrlWarnings([]);
    submitForm();
  };

  const handleCancelWarning = () => {
    setShowUrlWarningDialog(false);
    setUrlWarnings([]);
  };

  const handleCopyApiKey = () => {
    if (apiKey?.key) {
      navigator.clipboard.writeText(apiKey.key);
      enqueueSnackbar("API key copied to clipboard", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    }
  };

  const handleGetOrCreateApiKey = () => {
    if (!config?.id) return;

    // The backend will automatically create an API key if one doesn't exist
    getOrCreateApiKey({ _url: `/api-key/by-app-config/${config.id}` });
  };

  return (
    <Dialog
      open={open}
      onClose={dialogOnClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
      TransitionProps={{
        timeout: 200,
      }}
    >
      <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SettingsIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
            {isEdit ? "Edit Application Config" : "Create Application Config"}
          </Typography>
        </Stack>
      </DialogTitle>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <DialogContent
          sx={{
            pt: 0.875,
            pb: 0.875,
            overflow: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Stack spacing={2.5}>
            {/* Application Details Section */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 2,
                  color: "text.primary",
                }}
              >
                Application Details
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Name"
                  fullWidth
                  size="small"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Application"
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={textFieldSx}
                />
                <StyledFormControl fullWidth size="small" error={!!errors.cfg_type}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={formData.cfg_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cfg_type: e.target.value as AppConfigType,
                      })
                    }
                  >
                    <StyledMenuItem value="production">Production</StyledMenuItem>
                    <StyledMenuItem value="staging">Staging</StyledMenuItem>
                    <StyledMenuItem value="development">Development</StyledMenuItem>
                    <StyledMenuItem value="local">Local</StyledMenuItem>
                  </Select>
                </StyledFormControl>
              </Stack>
              <ValidatedUrlInput
                label="Base URL"
                fullWidth
                size="small"
                value={formData.base_url}
                onChange={(value) => setFormData({ ...formData, base_url: value })}
                onValidationChange={setBaseUrlValid}
                placeholder="example.com or https://example.com"
                error={!!errors.base_url}
                helperText={errors.base_url}
                required
                sx={{ mt: 2, fontSize: 14 }}
              />
            </Paper>

            {/* Login Credentials Section */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 1,
                  color: "text.primary",
                }}
              >
                Login Credentials
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                Define the login credentials so that the agent can login to your application.
              </Typography>

              <ValidatedUrlInput
                label="Login URL"
                fullWidth
                size="small"
                value={formData.login_url}
                onChange={(value) => setFormData({ ...formData, login_url: value })}
                onValidationChange={setLoginUrlValid}
                placeholder="example.com/login or https://example.com/login"
                error={!!errors.login_url}
                helperText={errors.login_url}
                sx={{ fontSize: 14 }}
              />

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextField
                  label="Login user / email"
                  fullWidth
                  size="small"
                  value={formData.login_username}
                  onChange={(e) => setFormData({ ...formData, login_username: e.target.value })}
                  placeholder="test@example.com"
                  sx={textFieldSx}
                />
                <TextField
                  label="Login password"
                  fullWidth
                  size="small"
                  type={showPassword ? "text" : "password"}
                  value={formData.login_password}
                  onChange={(e) => setFormData({ ...formData, login_password: e.target.value })}
                  placeholder="********"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label={showPassword ? "Unmask Password" : "Mask Password"}
                            onClick={() => setShowPassword((prev) => !prev)}
                          >
                            {showPassword ? (
                              <VisibilityOff sx={{ fontSize: 16 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={textFieldSx}
                />
              </Stack>

              <TextField
                label="MFA Secret (TOTP)"
                fullWidth
                size="small"
                type={showMfaSecret ? "text" : "password"}
                value={formData.login_mfa_secret}
                onChange={(e) => setFormData({ ...formData, login_mfa_secret: e.target.value })}
                placeholder="JBSWY3DPEHPK3PXP (base32 encoded secret)"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label={showMfaSecret ? "Unmask Secret" : "Mask Secret"}
                          onClick={() => setShowMfaSecret((prev) => !prev)}
                        >
                          {showMfaSecret ? (
                            <VisibilityOff sx={{ fontSize: 16 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={[textFieldSx, { mt: 2 }]}
              />

              <TextField
                label="(Additional) Login instructions"
                fullWidth
                size="small"
                multiline
                minRows={2}
                maxRows={3}
                value={formData.login_instructions}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    login_instructions: e.target.value,
                  })
                }
                placeholder="E.g. use the username and password to login..."
                sx={[textFieldSx, { mt: 2 }]}
              />
            </Paper>

            {/* Configuration Options Section */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 2,
                  color: "text.primary",
                }}
              >
                Configuration Options
              </Typography>

              <Stack direction="row" spacing={2}>
                <StyledFormControl fullWidth size="small">
                  <InputLabel>Viewport</InputLabel>
                  <Select
                    label="Viewport"
                    value={formData.vp_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        vp_type: e.target.value as ViewportType,
                      })
                    }
                  >
                    <StyledMenuItem value="big_desktop">Big Desktop (1920x1080)</StyledMenuItem>
                    <StyledMenuItem value="desktop">Desktop (1200x800)</StyledMenuItem>
                    <StyledMenuItem value="tablet">Tablet (768x1024)</StyledMenuItem>
                    <StyledMenuItem value="mobile">Mobile (360x800)</StyledMenuItem>
                  </Select>
                </StyledFormControl>

                <StyledFormControl fullWidth size="small">
                  <InputLabel>Failure Control</InputLabel>
                  <Select
                    label="Failure Control"
                    value={formData.failure_status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        failure_status: e.target.value as
                          | TestRunStatus.FAILED
                          | TestRunStatus.FAILED_PENDING,
                      })
                    }
                  >
                    <StyledMenuItem value={TestRunStatus.FAILED_PENDING}>
                      Review on failure
                    </StyledMenuItem>
                    <StyledMenuItem value={TestRunStatus.FAILED}>Fail immediately</StyledMenuItem>
                  </Select>
                </StyledFormControl>
              </Stack>

              <StyledFormControl fullWidth size="small" sx={{ mt: 2 }}>
                <InputLabel>Remove Pop-ups</InputLabel>
                <Select
                  label="Remove Pop-ups"
                  value={formData.remove_popups ? "remove" : "keep"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      remove_popups: e.target.value === "remove",
                    })
                  }
                >
                  <StyledMenuItem value="keep">Keep (default)</StyledMenuItem>
                  <StyledMenuItem value="remove">Remove</StyledMenuItem>
                </Select>
              </StyledFormControl>
            </Paper>

            {/* Advanced Settings Accordion */}
            <Accordion
              ref={advancedSettingsRef}
              expanded={advancedSettingsExpanded}
              onChange={(_, isExpanded) => {
                setAdvancedSettingsExpanded(isExpanded);
                if (isExpanded) {
                  setTimeout(() => {
                    const dialogContent =
                      advancedSettingsRef.current?.closest(".MuiDialogContent-root");
                    if (dialogContent) {
                      dialogContent.scrollTo({
                        top: dialogContent.scrollHeight,
                        behavior: "smooth",
                      });
                    }
                  }, 300);
                }
              }}
              elevation={0}
              sx={{
                bgcolor: "grey.50",
                borderRadius: 1,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                sx={{
                  px: 2,
                  py: 1,
                  minHeight: "unset",
                  "&.Mui-expanded": { minHeight: "unset" },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  Advanced Settings
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                <Stack spacing={2}>
                  <TextField
                    label="User Agent"
                    fullWidth
                    size="small"
                    value={formData.user_agent}
                    onChange={(e) => setFormData({ ...formData, user_agent: e.target.value })}
                    placeholder="Mozilla/5.0 (Windows NT 10.0; Win64; x64)..."
                    helperText="Custom user agent string for browser"
                    sx={textFieldSx}
                  />

                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Locale"
                      fullWidth
                      size="small"
                      value={formData.locale}
                      onChange={(e) => setFormData({ ...formData, locale: e.target.value })}
                      placeholder="en-US, es-ES, fr-FR, etc."
                      helperText="Browser locale (e.g., en-US)"
                      sx={textFieldSx}
                    />

                    <TextField
                      label="Timezone"
                      fullWidth
                      size="small"
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      placeholder="America/New_York, Europe/London, etc."
                      helperText="Timezone ID (e.g., America/New_York)"
                      sx={textFieldSx}
                    />
                  </Stack>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      mt: 1,
                    }}
                  >
                    Geolocation
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Latitude"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.geolocation_latitude}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          geolocation_latitude: e.target.value,
                        })
                      }
                      placeholder="40.7128"
                      helperText="Latitude coordinate"
                      slotProps={{
                        input: {
                          inputProps: { step: "0.000001" },
                        },
                      }}
                      sx={textFieldSx}
                    />

                    <TextField
                      label="Longitude"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.geolocation_longitude}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          geolocation_longitude: e.target.value,
                        })
                      }
                      placeholder="-74.0060"
                      helperText="Longitude coordinate"
                      slotProps={{
                        input: {
                          inputProps: { step: "0.000001" },
                        },
                      }}
                      sx={textFieldSx}
                    />
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.primary", fontFamily: "monospace" }}
                    >
                      {config?.id || "N/A"}
                    </Typography>
                  </Typography>
                  {config?.id && (
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(config.id);
                        enqueueSnackbar("App Config ID copied to clipboard", {
                          variant: "success",
                          anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                          },
                        });
                      }}
                      size="small"
                      sx={{
                        color: "grey.600",
                        "&:hover": {
                          bgcolor: "grey.100",
                        },
                        fontSize: 12,
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </DialogContent>
      </form>

      <DialogActions
        sx={{
          p: 0.875,
          pr: 3,
          justifyContent: "flex-end",
          flexShrink: 0,
          borderTop: "0",
          bgcolor: "background.paper",
        }}
      >
        {/* API Key Section */}
        <Paper elevation={0} sx={{ p: 2 }}>
          {isEdit && config?.id ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 1,
                  color: "text.secondary",
                }}
              >
                API Key
              </Typography>
              {hasApiKey ? (
                <IconButton
                  onClick={handleCopyApiKey}
                  size="small"
                  sx={{
                    color: "grey.600",
                    "&:hover": {
                      bgcolor: "grey.100",
                    },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : (
                <IconButton
                  onClick={handleGetOrCreateApiKey}
                  disabled={isGettingApiKey}
                  size="small"
                  sx={{
                    color: "grey.600",
                    "&:hover": {
                      bgcolor: "grey.100",
                    },
                    "&:disabled": {
                      color: "grey.400",
                    },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              API key can only be accessed after creating a configuration.
            </Typography>
          )}
        </Paper>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          variant="outlined"
          size="small"
          startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: "grey.600",
            borderColor: "grey.300",
            "&:hover": {
              bgcolor: "grey.50",
              borderColor: "grey.400",
            },
            "&:disabled": {
              bgcolor: "grey.100",
              color: "grey.400",
              borderColor: "grey.200",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>

      {/* URL Validation Warning Dialog */}
      <Dialog
        open={showUrlWarningDialog}
        onClose={handleCancelWarning}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <WarningIcon sx={{ color: "warning.main", fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
              URL Validation Warning
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            The following issues were detected:
          </Typography>
          {urlWarnings.map((warning, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 1, pl: 2, color: "text.primary" }}>
              • {warning}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary", fontStyle: "italic" }}>
            Tests may fail if the URLs are unreachable. Do you want to save anyway?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelWarning}
            variant="outlined"
            size="small"
            sx={{
              color: "grey.600",
              borderColor: "grey.300",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmWithWarnings}
            variant="contained"
            size="small"
            color="warning"
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
